// ---------------------------------------------------------------------------
// «استلام الحساب» - THE account-collection dialog, shared verbatim.
//
// The POS quick panel and the full Orders & Delivery page both open exactly
// this component, which is the whole of the "one payment engine" rule at the
// UI layer: one key-minting policy, one mutation, one error surface, one
// toast - two buttons in two places, zero duplicated collection logic.
//
// What it wires and what it refuses to wire:
//
//   * the canonical PaymentDialog runs on the order's FROZEN figures - the
//     frozen lira total and the frozen dispatch-time rate - never on the
//     active cart's totals and never on today's rate;
//   * fulfilment is shown locked as DELIVERY and never asked; «بدون تحضير»
//     never appears - collection is money only, everything else is history;
//   * ONE idempotency key is minted per opening, so a double-click or a slow
//     retry settles once; the server's UNPAID-guard stays the last word;
//   * the dialog is titled with the ORDER NUMBER, so the cashier can see
//     whose money they are recording before they record it.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import { useCollectDelivery } from '../../api/customers.api';
import { usePublicSettings } from '../../api/system.api';
import { useCurrentUser } from '../../hooks/usePermissions';
import { ApiError } from '../../lib/apiClient';
import { toast } from '../../stores/toastStore';
import { PaymentDialog } from './PaymentDialog';
import { NetworkPrintSheets } from './NetworkPrintSheets';
import type { PrintPlan } from '../../hooks/useSalePrinting';
import type { Currency, CustomerOrder, PaymentDraft } from '../../types';

function newCollectionKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `collect-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function CollectAccountDialog({
  order,
  onClose,
}: {
  /// The delivery whose driver has just come back. Null keeps the dialog
  /// closed; passing an order opens it and mints the key for this attempt.
  order: CustomerOrder | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const collect = useCollectDelivery();
  const { data: settings } = usePublicSettings();
  const me = useCurrentUser();
  const [printedSale, setPrintedSale] = useState<{ orderId: string; sale: import('../../types').Sale; plan: PrintPlan } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState('');

  // One safety key per ORDER OPENED - a retry inside this dialog reuses it
  // (the server returns the first sale), and the next delivery gets its own.
  // Keyed on the id, deliberately: a background refetch handing down a fresh
  // object for the SAME order must not mint a second key mid-collection.
  const orderId = order?.id ?? null;
  useEffect(() => {
    setPrintedSale(null);
    if (!orderId) return;
    setError(null);
    setKey(newCollectionKey());
  }, [orderId]);

  const submit = async (payments: PaymentDraft[], changeCurrency: Currency, skipReceipt = false) => {
    if (!order) return;
    setError(null);

    try {
      const response = await collect.mutateAsync({
        orderId: order.id,
        payments,
        changeCurrency,
        idempotencyKey: key,
        ...(skipReceipt ? { skipReceipt: true } : {}),
      });
      if (response.printPlan?.documents.length) setPrintedSale({ orderId: order.id, sale: response.sale, plan: response.printPlan });
      else onClose();
      toast.success(
        t.delivery.accountReceived,
        `${response.order.orderNumber} — ${response.sale.receiptNumber}`,
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody);
    }
  };

  if (!order) return null;

  // The money joins the SENDER's drawer. When that is somebody else, say so
  // before a single note is counted - the owner or a manager receiving for a
  // cashier must not think it lands in their own shift.
  const sender = order.shift?.cashier ?? null;
  const receivingFor = sender && me && sender.id !== me.id ? sender : null;

  if (printedSale?.orderId === order.id) return <NetworkPrintSheets plan={printedSale.plan} sale={printedSale.sale} onSettled={onClose} shop={{ nameEn: settings?.['business.name_en'] ?? 'Yaghi Butcher', nameAr: settings?.['business.name_ar'] ?? 'ملحمة ياغي', address: settings?.['business.address'] ?? '', phone: settings?.['business.phone'] ?? '' }} />;

  return (
    <PaymentDialog
      open
      onClose={onClose}
      title={`${t.delivery.collectAccount} — ${order.orderNumber}`}
      notice={
        receivingFor ? (
          <div role="note" className="mb-4 rounded-card border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">{t.delivery.receivingForTitle.replace('{name}', receivingFor.fullName)}</p>
            <p className="mt-0.5">
              {t.delivery.receivingForBody
                .replace('{name}', receivingFor.fullName)
                .replace('{shift}', order.shift?.shiftNumber ?? '')}
            </p>
          </div>
        ) : null
      }
      totalUsd={order.estimatedTotalUsd}
      totalLbp={order.totalLbp}
      exchangeRate={order.exchangeRateUsdToLbp}
      submitting={collect.isPending}
      errorMessage={error}
      deliveryActive={order.fulfilment === 'DELIVERY'}
      deliveryFeeLbp={order.deliveryFeeLbp}
      orderFulfilment={order.fulfilment}
      // Only a pickup's settlement prints a receipt; a delivery's paper left
      // with the driver, so «بدون إيصال» would decide nothing there.
      receiptWillPrint={order.fulfilment === 'PICKUP'}
      onSubmit={(payments, changeCurrency, _reach, _skipPreparation, skipReceipt) => void submit(payments, changeCurrency, skipReceipt)}
    />
  );
}
